import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  FileText,
  Kanban,
  FolderOpen,
  BarChart3,
  Sparkles,
  Target,
  Settings,
  Bell,
  Building2,
  Brain,
  Trophy,
  Eye,
  GitBranch,
  Briefcase,
  ShoppingCart,
  LogOut,
  Handshake,
  Zap,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { getAppVersion } from "@/lib/runtime-config";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/hooks/use-tenant";
import { watchlistService } from "@/services/watchlist.service";


type NavItem = {
  to: string;
  label: string;
  icon: typeof FileText;
  badge?: string;
  badgeKind?: "primary" | "muted";
};

const nav: Array<{ group: string; items: NavItem[] }> = [
  {
    group: "Inteligencia",
    items: [
      {
        to: "/oportunidades",
        label: "Oportunidades IA",
        icon: Sparkles,
        badge: "Nuevo",
        badgeKind: "primary" as const,
      },
      {
        to: "/compra-agil-ia",
        label: "Compra Ágil IA",
        icon: Zap,
        badge: "Nuevo",
        badgeKind: "primary" as const,
      },
      { to: "/dashboard-ia", label: "Dashboard IA", icon: Brain },
      { to: "/decisiones", label: "Decisiones", icon: GitBranch },
      { to: "/watchlist", label: "Watchlist", icon: Eye },
      { to: "/competidores", label: "Competidores", icon: Trophy },
      { to: "/empresa", label: "Perfil Empresa", icon: Briefcase },
    ],
  },
  {
    group: "Operación",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/licitaciones", label: "Licitaciones", icon: FileText },
      { to: "/ordenes-compra", label: "Órdenes de Compra", icon: ShoppingCart },
      { to: "/convenios-marco", label: "Convenio Marco", icon: Handshake },
      { to: "/compra-agil", label: "Compra Ágil", icon: Zap },
      { to: "/crm", label: "Pipeline CRM", icon: Kanban },
      { to: "/documentos", label: "Documentos", icon: FolderOpen },
    ],
  },
  {
    group: "Análisis",
    items: [
      { to: "/ia", label: "Motor IA", icon: Sparkles },
      { to: "/bi", label: "Business Intelligence", icon: BarChart3 },
      { to: "/perfilamiento", label: "Perfilamiento", icon: Target },
    ],
  },
  {
    group: "Sistema",
    items: [
      { to: "/notificaciones", label: "Notificaciones", icon: Bell, badge: "3" },
      { to: "/configuracion", label: "Configuración", icon: Settings },
    ],
  },
];

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const appVersion = getAppVersion();
  const { session, logout } = useAuth();
  const { tenantName, loading: tenantLoading } = useTenant();
  // Badge real de la watchlist: cantidad de alertas vivas del tenant.
  const alertas = useQuery({
    queryKey: ["watchlist", "alerts"],
    queryFn: watchlistService.alerts,
    staleTime: 60_000,
    retry: false,
  });
  const watchlistAlerts = alertas.data?.length ?? 0;


  const userNombre = session?.user?.name || "Usuario";
  const userEmail = session?.user?.email || "";
  const userInicial =
    userNombre
      .split(" ")
      .map((p) => p[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-4 pt-4 pb-3 border-b border-sidebar-border">
        <Link to="/" onClick={onNavigate} className="flex items-center gap-2 mb-3">
          <img
            src="/isotipo-licita.svg"
            alt="LicitaPro"
            className="size-8 rounded-lg shadow-glow"
          />
          <div>
            <div className="font-display font-semibold text-sidebar-foreground leading-tight">
              LicitaPro
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Intelligence{appVersion ? ` · v${appVersion}` : ""}
            </div>
          </div>
        </Link>
        <div className="w-full flex items-center gap-2 rounded-md border border-sidebar-border bg-sidebar-accent/40 px-2.5 py-2 text-left">
          <Building2 className="size-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            {tenantLoading && !tenantName ? (
              <div className="h-3.5 w-28 rounded bg-muted animate-pulse" />
            ) : (
              <>
                <div className="text-xs font-medium text-sidebar-foreground truncate">
                  {tenantName ?? "Sin empresa asignada"}
                </div>
                <div className="text-[10px] text-muted-foreground">Cuenta TGT One</div>
              </>
            )}
          </div>
        </div>

      </div>

      <nav
        aria-label="Navegación principal"
        className="flex-1 min-h-0 overflow-y-auto px-2 py-3 space-y-5"
      >
        {nav.map((g) => (
          <div key={g.group}>
            <div className="px-2 mb-1.5 text-[10px] uppercase tracking-wider font-medium text-muted-foreground flex items-center gap-1.5">
              {g.group}
              {g.group === "Inteligencia" && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-primary/15 text-primary font-semibold">
                  IA
                </span>
              )}
            </div>
            <ul className="space-y-0.5">
              {g.items.map((item) => {
                const it =
                  item.to === "/watchlist" && watchlistAlerts > 0
                    ? { ...item, badge: String(watchlistAlerts) }
                    : item;
                const active = it.to === "/" ? pathname === "/" : pathname.startsWith(it.to);
                const Icon = it.icon;
                const badgeKind = (it as { badgeKind?: "primary" | "muted" }).badgeKind ?? "muted";
                return (
                  <li key={it.to}>
                    <Link
                      to={it.to}
                      onClick={onNavigate}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-md px-2.5 py-2 md:py-1.5 text-sm transition relative",
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                          : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/60",
                      )}
                    >
                      {active && (
                        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-sidebar-primary" />
                      )}
                      <Icon
                        className={cn(
                          "size-4 shrink-0",
                          active
                            ? "text-sidebar-primary"
                            : "text-muted-foreground group-hover:text-sidebar-foreground",
                        )}
                      />
                      <span className="flex-1 truncate">{it.label}</span>
                      {it.badge && (
                        <span
                          className={cn(
                            "text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                            badgeKind === "primary"
                              ? "bg-primary/15 text-primary"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {it.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-sidebar-border space-y-3">
        <Link
          to="/oportunidades"
          onClick={onNavigate}
          className="w-full flex items-center justify-center gap-1.5 rounded-md bg-gradient-primary text-primary-foreground text-sm font-medium px-3 py-2 shadow-glow hover:opacity-90 transition"
        >
          <Sparkles className="size-4" />
          Ver oportunidades IA
        </Link>
        <div className="flex items-center gap-2.5 px-1">
          <div className="size-8 rounded-full bg-gradient-primary grid place-items-center text-primary-foreground text-xs font-semibold shrink-0">
            {userInicial}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-sidebar-foreground truncate">{userNombre}</div>
            <div className="text-[10px] text-muted-foreground truncate">{userEmail}</div>
          </div>
          <button
            onClick={logout}
            className="size-8 grid place-items-center rounded-md hover:bg-sidebar-accent text-muted-foreground hover:text-destructive transition shrink-0"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <LogOut className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
